import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Video, Crown, ChevronRight, X } from 'lucide-react';
import { api } from '@/contexts/AuthContext';

/**
 * Orchestrated nudge banner. Picks ONE CTA based on the user's biggest gap
 * (signal-rank: not verified > no profile video > free tier).
 *
 * Dismissible. Per-nudge dismissal is remembered for 24h via localStorage so
 * we don't nag the same person twice in a session.
 *
 * Usage:
 *   <ProfileBoostBanner />     — discover/profile pages
 *   <ProfileBoostBanner compact /> — for tighter spots
 */
const STORAGE_KEY = 'mtk_boost_dismissed_v1';
const ONE_DAY = 24 * 60 * 60 * 1000;

const NUDGES = {
  verify: {
    id: 'verify',
    label: 'Verified profiles get 3× more matches',
    cta: 'Verify now',
    href: '/verify',
    icon: ShieldCheck,
    accent: '#0EA5A4',
    accentBg: 'rgba(14,165,164,0.08)',
    eyebrow: 'GET VERIFIED',
  },
  video: {
    id: 'video',
    label: 'Add a 15-sec intro video — up to 2× more matches',
    cta: 'Add video',
    href: '/profile?focus=videos',
    icon: Video,
    accent: '#B91C1C',
    accentBg: 'rgba(185,28,28,0.08)',
    eyebrow: 'STAND OUT',
  },
  upgrade: {
    id: 'upgrade',
    label: 'Premium members get 5× more matches',
    cta: 'Go Premium',
    href: '/subscription',
    icon: Crown,
    accent: '#B38138',
    accentBg: 'rgba(212,169,96,0.10)',
    eyebrow: 'PREMIUM',
  },
};

function readDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const map = JSON.parse(raw);
    const now = Date.now();
    return Object.fromEntries(Object.entries(map).filter(([, ts]) => now - ts < ONE_DAY));
  } catch {
    return {};
  }
}

function writeDismissed(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch (_) {}
}

export default function ProfileBoostBanner({ compact = false, profile: propProfile, sub: propSub }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(propProfile || null);
  const [sub, setSub] = useState(propSub || null);
  const [dismissed, setDismissed] = useState(readDismissed());

  useEffect(() => {
    if (!propProfile) {
      api.get('/profile').then(r => setProfile(r.data || null)).catch(() => {});
    }
    if (!propSub) {
      api.get('/subscriptions/my').then(r => setSub(r.data || null)).catch(() => {});
    }
  }, [propProfile, propSub]);

  if (!profile) return null;

  const isVerified = (profile.badges || []).includes('verified') ||
                     (profile.badges || []).includes('selfie_verified') ||
                     profile.is_verified === true;
  const hasVideo = Array.isArray(profile.videos) && profile.videos.length > 0;
  const isPaid = sub?.tier === 'premium' || sub?.tier === 'vip';

  // Priority order: missing verification > missing video > free tier
  let nudge = null;
  if (!isVerified) nudge = NUDGES.verify;
  else if (!hasVideo) nudge = NUDGES.video;
  else if (!isPaid) nudge = NUDGES.upgrade;

  if (!nudge || dismissed[nudge.id]) return null;

  const Icon = nudge.icon;

  const handleDismiss = (e) => {
    e.stopPropagation();
    const next = { ...dismissed, [nudge.id]: Date.now() };
    setDismissed(next);
    writeDismissed(next);
  };

  const handleClick = () => {
    navigate(nudge.href);
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`boost-banner-${nudge.id}`}
        className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--hairline-strong)] bg-white hover:bg-black/[0.02] transition-colors text-left">
        <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: nudge.accentBg }}>
          <Icon size={13} strokeWidth={2} color={nudge.accent} />
        </span>
        <span className="text-[12px] font-semibold text-[#0F0F10] flex-1 truncate">{nudge.label}</span>
        <ChevronRight size={14} className="text-[#8A8A93] flex-shrink-0" />
      </button>
    );
  }

  return (
    <div
      data-testid={`boost-banner-${nudge.id}`}
      onClick={handleClick}
      className="relative rounded-2xl border bg-white px-4 py-3 cursor-pointer hover:shadow-[0_4px_14px_-4px_rgba(15,15,16,0.10)] transition-shadow"
      style={{ borderColor: 'var(--hairline-strong)' }}>
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: nudge.accentBg }}>
          <Icon size={18} strokeWidth={1.8} color={nudge.accent} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: nudge.accent }}>
            {nudge.eyebrow}
          </p>
          <p className="text-[13px] font-semibold text-[#0F0F10] truncate">{nudge.label}</p>
        </div>
        <span
          className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full text-white flex-shrink-0 hidden sm:inline-flex items-center gap-1"
          style={{ backgroundColor: nudge.accent }}>
          {nudge.cta} <ChevronRight size={12} />
        </span>
        <button
          type="button"
          data-testid="boost-banner-dismiss"
          onClick={handleDismiss}
          className="w-7 h-7 -mr-1 rounded-full text-[#8A8A93] hover:bg-black/[0.04] flex items-center justify-center transition-colors flex-shrink-0">
          <X size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
